import { useEffect, useMemo, useState } from 'react';
import clienteAxios from '../../config/axios';
import BANCOS_PANAMA from '../../config/bancosPanama';
import { useT } from 'i18n';

// bank_personal: '1' = cuenta nacional (Panamá), '2' = cuenta internacional. Es el mapeo que ya usa el resto del portal.
const TIPOS_CUENTA = ['Ahorros', 'Corriente'];
const TIPO_CLAVE = { Ahorros: 'modal.cAhorros', Corriente: 'modal.cCorriente' };

const base = { bank_client: '', bank_name: '', bank_account: '', bank_type: 'Ahorros', bank_swift: '', bank_aba: '', country_id: '' };

const ModalNuevaCuenta = ({ abierto, tipo = 'nacional', onCerrar, onGuardada }) => {
  const { t, ts } = useT();
  const internacional = tipo === 'internacional';
  const [form, setForm] = useState(base);
  const [paises, setPaises] = useState([]);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto) return undefined;
    setForm(base);
    setError('');
    let activo = true;
    clienteAxios.post('/api/paises/list')
      .then((r) => { if (activo) setPaises(r.data.data || []); })
      .catch(() => {});
    return () => { activo = false; };
  }, [abierto, internacional]);

  const listaPaises = useMemo(
    () => [...paises].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es')),
    [paises]
  );

  if (!abierto) return null;

  const cambiar = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const guardar = async (e) => {
    e.preventDefault();
    const cuenta = form.bank_account.replace(/\s+/g, '');
    if (cuenta.length < 5) { setError(t('modal.cCuentaCorta')); return; }
    if (internacional && !form.country_id) { setError(t('modal.cSelPais')); return; }
    setGuardando(true);
    setError('');
    try {
      const datos = {
        bank_client: form.bank_client.trim(),
        bank_name: form.bank_name.trim(),
        bank_account: cuenta,
        bank_type: form.bank_type,
        bank_personal: internacional ? '2' : '1',
      };
      if (internacional) {
        datos.country_id = form.country_id;
        datos.bank_swift = form.bank_swift.trim().toUpperCase();
        datos.bank_aba = form.bank_aba.trim();
      } else {
        const pa = paises.find((c) => /^panam/i.test(c.name || ''));
        datos.country_id = pa ? pa.id : '';
        datos.bank_swift = '';
      }
      await clienteAxios.post('/api/client/beneficiario_create', datos);
      onGuardada && onGuardada();
    } catch (err) {
      setError(ts(err.response?.data?.message) || t('modal.cNoGuardada'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="p7-wallet-overlay" role="dialog" aria-modal="true" onClick={onCerrar}>
      <form className="p7-wallet-card p7-wallet-ancha" onClick={(e) => e.stopPropagation()} onSubmit={guardar}>
        <h4>{internacional ? t('modal.cTituloInt') : t('modal.cTituloNac')}</h4>
        <p className="p7-wallet-help">
          {internacional
            ? t('modal.cAyudaInt')
            : t('modal.cAyudaNac')}
        </p>

        <div className="p7-wallet-grid">
          <div className="p7-col2">
            <label htmlFor="p7-c-titular">{t('modal.cTitular')}</label>
            <input id="p7-c-titular" name="bank_client" value={form.bank_client} onChange={cambiar} maxLength={80} required autoComplete="name" />
          </div>

          {internacional && (
            <div>
              <label htmlFor="p7-c-pais">{t('modal.cPais')}</label>
              <select id="p7-c-pais" name="country_id" value={form.country_id} onChange={cambiar} required>
                <option value="">{t('modal.cSelPaisOpt')}</option>
                {listaPaises.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}

          <div className={internacional ? '' : 'p7-col2'}>
            <label htmlFor="p7-c-banco">{t('modal.banco')}</label>
            <input id="p7-c-banco" name="bank_name" value={form.bank_name} onChange={cambiar} list={internacional ? undefined : 'p7-bancos-pa'} placeholder={internacional ? '' : t('modal.cBancoPh')} maxLength={80} required />
            {!internacional && (
              <datalist id="p7-bancos-pa">
                {BANCOS_PANAMA.map((b) => <option key={b} value={b} />)}
              </datalist>
            )}
          </div>

          <div>
            <label htmlFor="p7-c-cuenta">{t('modal.numCuenta')}</label>
            <input id="p7-c-cuenta" name="bank_account" value={form.bank_account} onChange={cambiar} maxLength={40} inputMode="numeric" required autoComplete="off" />
          </div>

          <div>
            <label htmlFor="p7-c-tipo">{t('modal.tipoCuenta')}</label>
            <select id="p7-c-tipo" name="bank_type" value={form.bank_type} onChange={cambiar}>
              {TIPOS_CUENTA.map((tc) => <option key={tc} value={tc}>{t(TIPO_CLAVE[tc])}</option>)}
            </select>
          </div>

          {internacional && (
            <>
              <div>
                <label htmlFor="p7-c-swift">{t('modal.cSwift')}</label>
                <input id="p7-c-swift" name="bank_swift" value={form.bank_swift} onChange={cambiar} maxLength={11} required autoComplete="off" spellCheck={false} />
              </div>
              <div>
                <label htmlFor="p7-c-aba">{t('modal.cAba')}</label>
                <input id="p7-c-aba" name="bank_aba" value={form.bank_aba} onChange={cambiar} maxLength={20} autoComplete="off" />
              </div>
            </>
          )}
        </div>

        {error && <div className="p7-wallet-error">{error}</div>}

        <div className="p7-wallet-actions">
          <button type="button" className="p7-btn-ghost" onClick={onCerrar}>{t('modal.cancelar')}</button>
          <button type="submit" className="p7-btn-gold" disabled={guardando}>{guardando ? t('modal.guardando') : t('modal.cGuardar')}</button>
        </div>
      </form>
    </div>
  );
};

export default ModalNuevaCuenta;
